import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function IssueCard({ issue, onReview }) {
  return (
    <Box
      sx={{
        p: 1.45,
        borderRadius: 3,
        border: "1px solid rgba(25,36,47,.10)",
        bgcolor: "#fff",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" alignItems="center">
            <Chip
              size="small"
              label={issue.category}
              color={issue.severity === "warning" ? "warning" : "default"}
              variant={issue.severity === "warning" ? "filled" : "outlined"}
              sx={{ height: 23, fontWeight: 900 }}
            />
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)", fontWeight: 800 }}>
              {issue.sectionLabel}
            </Typography>
          </Stack>
          <Typography sx={{ mt: 0.9, color: BRAND.dark, fontWeight: 950, lineHeight: 1.22 }}>
            {issue.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.6, color: "rgba(25,36,47,.68)", lineHeight: 1.55 }}>
            {issue.description}
          </Typography>
          {issue.detail ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.55, color: "rgba(25,36,47,.52)" }}>
              {issue.detail}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      {issue.section ? (
        <Button
          size="small"
          variant="outlined"
          startIcon={<TuneRoundedIcon />}
          onClick={() => onReview?.(issue)}
          aria-label={`Revisar ${issue.title}${issue.sectionLabel ? ` en ${issue.sectionLabel}` : ""}`}
          sx={{
            mt: 1.15,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 900,
            color: BRAND.dark,
            borderColor: "rgba(25,36,47,.18)",
          }}
        >
          Revisar en el editor
        </Button>
      ) : null}
    </Box>
  );
}

export default function LandingAccessibilityDrawer({
  open,
  audit,
  onClose,
  onReviewIssue,
}) {
  const issues = Array.isArray(audit?.issues) ? audit.issues : [];
  const warnings = Number(audit?.warnings || 0);
  const suggestions = Number(audit?.suggestions || 0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 430 },
          maxWidth: "100vw",
          height: { xs: "100dvh", sm: "100%" },
          maxHeight: "100dvh",
          boxSizing: "border-box",
          overflowX: "hidden",
          pb: { xs: "env(safe-area-inset-bottom)", sm: 0 },
          bgcolor: "#F7F8FA",
          "& .MuiButton-root": { minHeight: { xs: 44, sm: 36 } },
          "& .MuiIconButton-root": { minWidth: { xs: 44, sm: 40 }, minHeight: { xs: 44, sm: 40 } },
        },
      }}
    >
      <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: "rgba(247,248,250,.97)", backdropFilter: "blur(16px)" }}>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ px: 2, py: 1.6 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              color: BRAND.orange,
              bgcolor: "rgba(243,90,58,.10)",
              flexShrink: 0,
            }}
          >
            <AccessibilityNewRoundedIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.15 }}>
              Revisión de accesibilidad
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
              Señales orientativas sobre el borrador actual
            </Typography>
          </Box>
          <IconButton aria-label="Cerrar revisión de accesibilidad" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Divider />
      </Box>

      <Stack spacing={1.5} sx={{ p: 2, pb: 4 }}>
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Esta revisión no publica, no modifica el diseño y no bloquea contenido válido. Usala como ayuda para decidir qué conviene ajustar.
        </Alert>

        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
          <Chip size="small" label={`${warnings} para revisar`} color={warnings ? "warning" : "default"} sx={{ fontWeight: 900 }} />
          <Chip size="small" label={`${suggestions} sugerencias`} variant="outlined" sx={{ fontWeight: 900 }} />
        </Stack>

        {!issues.length ? (
          <Box
            sx={{
              p: 2.2,
              textAlign: "center",
              borderRadius: 3,
              bgcolor: "#fff",
              border: "1px solid rgba(25,36,47,.09)",
            }}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: 42, color: "success.main" }} />
            <Typography sx={{ mt: 0.8, color: BRAND.dark, fontWeight: 950 }}>
              No encontramos señales básicas pendientes
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(25,36,47,.62)" }}>
              Esto no reemplaza una auditoría especializada, pero cubre los problemas más comunes que puede introducir el WebBuilder.
            </Typography>
          </Box>
        ) : (
          issues.map((issue) => (
            <IssueCard key={issue.key} issue={issue} onReview={onReviewIssue} />
          ))
        )}
      </Stack>
    </Drawer>
  );
}
